/**
 * @file: brandModel.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 *
 * Normalize a vehicle's (brand, model) so the brand renders exactly once.
 * `vehicleModel` sometimes already embeds the brand (e.g. "福特F-150"), which
 * would otherwise read as "福特 · 福特F-150"; strip the leading brand prefix.
 * One source of truth for the "brand · model" badge shown on lead / reservation
 * cards, feed previews, plan cells, etc.
 */

export interface VehicleBrandModel {
  brand?: string;
  model?: string;
}

/**
 * The normalized (brand, model) pair: both trimmed, and the brand prefix
 * stripped from the model when the model already embeds it. Empty inputs
 * collapse to `undefined`.
 */
export function normalizeVehicleBrandModel(
  brandName?: string | null,
  model?: string | null,
): VehicleBrandModel {
  const brand = brandName?.trim() || undefined;
  const rawModel = model?.trim() || undefined;
  const normalizedModel =
    brand && rawModel && rawModel.startsWith(brand)
      ? rawModel.slice(brand.length).trim() || undefined
      : rawModel;
  return { brand, model: normalizedModel };
}

/**
 * Render the "brand · model" badge from the normalized pair. Falls back to
 * whichever part is present, and to `''` when neither is. `separator` defaults
 * to the middot used across the cards.
 */
export function formatVehicleBrandModel(
  brandName?: string | null,
  model?: string | null,
  separator = ' · ',
): string {
  const { brand, model: normalizedModel } = normalizeVehicleBrandModel(
    brandName,
    model,
  );
  return [brand, normalizedModel]
    .filter((s): s is string => !!s)
    .join(separator);
}
